import { ErrorMessage } from '@/components/ui/errormessage'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { kbdFieldProps } from '@/hooks/formKeyboardNav'
import { useEffect, useMemo, useState } from 'react'

export interface SelectOption {
	value: string | number
	label: string
}

export interface CustomDurationInputProps<T extends string | number | null = string | number | null> {
	value: T | undefined
	onChange: (value: T) => void
	options?: SelectOption[]
	placeholder?: string
	customInputPlaceholder?: string
	disabled?: boolean
	valueType?: 'string' | 'number'
	baseUnit?: 'seconds' | 'minutes' | 'hours' | 'days'
	customUnits?: Array<'seconds' | 'minutes' | 'hours' | 'days'>
	unit?: string
	/** Numeric mode: emit null for an empty custom box instead of 0, so callers can tell them apart. */
	emptyAsNull?: boolean
	/** Rendered under the number box in custom mode, under the select otherwise. */
	error?: string
	'data-testid'?: string
	'aria-invalid'?: boolean
}

const SECONDS_MULTIPLIERS = {
	seconds: 1,
	minutes: 60,
	hours: 3600,
	days: 86400,
}

function decomposeValue(
	value: number,
	baseUnit: keyof typeof SECONDS_MULTIPLIERS,
	customUnits: Array<keyof typeof SECONDS_MULTIPLIERS>,
) {
	const valueInSeconds = value * SECONDS_MULTIPLIERS[baseUnit]
	const sortedUnits = [...customUnits].sort((a, b) => SECONDS_MULTIPLIERS[b] - SECONDS_MULTIPLIERS[a])

	for (const unit of sortedUnits) {
		const multiplier = SECONDS_MULTIPLIERS[unit]
		if (valueInSeconds % multiplier === 0) {
			return {
				value: String(valueInSeconds / multiplier),
				unit,
			}
		}
	}

	const smallestUnit = sortedUnits[sortedUnits.length - 1] || baseUnit
	return {
		value: String(valueInSeconds / SECONDS_MULTIPLIERS[smallestUnit]),
		unit: smallestUnit,
	}
}

function recomposeValue(
	numStr: string,
	unit: keyof typeof SECONDS_MULTIPLIERS,
	baseUnit: keyof typeof SECONDS_MULTIPLIERS,
) {
	const num = Number(numStr)
	if (Number.isNaN(num)) {
		return 0
	}
	const valueInSeconds = num * SECONDS_MULTIPLIERS[unit]
	return Math.round(valueInSeconds / SECONDS_MULTIPLIERS[baseUnit])
}

export function CustomDurationInput<T extends string | number | null = string | number | null>({
	value,
	onChange,
	options = [],
	placeholder = 'Select duration',
	customInputPlaceholder = 'Enter number',
	disabled = false,
	valueType = 'string',
	baseUnit = 'days',
	customUnits = ['days'],
	unit = 'days',
	emptyAsNull = false,
	error,
	'data-testid': dataTestId,
	'aria-invalid': ariaInvalid,
}: CustomDurationInputProps<T>) {
	const [customValue, setCustomValue] = useState('')
	const [customUnit, setCustomUnit] = useState<keyof typeof SECONDS_MULTIPLIERS>(customUnits[0] || 'days')
	const [isCustomSelected, setIsCustomSelected] = useState(false)

	// Determine if custom mode is currently active
	const isCustomActive = useMemo(() => {
		if (valueType === 'string') {
			const strVal = String(value ?? '')
			return strVal.startsWith('custom-') || strVal === 'custom'
		}
		if (isCustomSelected) {
			return true
		}
		if (value === null || value === undefined || value === '') {
			return false
		}
		// If numeric value doesn't match any preset options, treat as custom active
		const matchesPreset = options.some(opt => {
			if (opt.value === 'custom') {
				return false
			}
			return Number(opt.value) === Number(value)
		})
		return !matchesPreset
	}, [value, valueType, isCustomSelected, options])

	// Sync state from value changes
	useEffect(() => {
		if (!isCustomActive) {
			return
		}

		if (valueType === 'string') {
			const strVal = String(value ?? '')
			if (strVal.startsWith('custom-')) {
				const parts = strVal.split('-')
				const currentNum = parts[1] || ''
				const currentU = (parts[2] || customUnits[0] || 'days') as keyof typeof SECONDS_MULTIPLIERS

				if (customValue !== currentNum || customUnit !== currentU) {
					setCustomValue(currentNum)
					setCustomUnit(currentU)
				}
			}
		} else {
			const numVal = value === null || value === undefined || value === '' ? 0 : Number(value)
			const currentRecomposed = recomposeValue(customValue, customUnit, baseUnit)

			if (currentRecomposed !== numVal) {
				const decomp = decomposeValue(numVal, baseUnit, customUnits)
				setCustomValue(decomp.value)
				setCustomUnit(decomp.unit)
			}
		}
	}, [value, valueType, isCustomActive, baseUnit, customUnits, customValue, customUnit])

	const handleSelectChange = (val: string) => {
		if (val === 'custom') {
			setIsCustomSelected(true)
			if (valueType === 'string') {
				if (customUnits.length > 1) {
					onChange(`custom--${customUnit}` as unknown as T)
				} else {
					onChange('custom-' as unknown as T)
				}
			} else {
				onChange(null as unknown as T)
			}
		} else {
			setIsCustomSelected(false)
			if (valueType === 'string') {
				onChange(val as unknown as T)
			} else {
				const num = Number(val)
				onChange((Number.isNaN(num) ? null : num) as unknown as T)
			}
		}
	}

	const handleCustomNumberChange = (numStr: string) => {
		setCustomValue(numStr)
		// Typing here keeps custom mode even when the box empties to a null value.
		setIsCustomSelected(true)
		if (valueType === 'string') {
			if (customUnits.length > 1) {
				onChange(`custom-${numStr}-${customUnit}` as unknown as T)
			} else {
				onChange(`custom-${numStr}` as unknown as T)
			}
		} else if (emptyAsNull && numStr.trim() === '') {
			// Empty box is "no value", not zero — callers need the two apart to validate.
			onChange(null as unknown as T)
		} else {
			const recomposed = recomposeValue(numStr, customUnit, baseUnit)
			onChange(recomposed as unknown as T)
		}
	}

	const handleCustomUnitChange = (nextUnit: keyof typeof SECONDS_MULTIPLIERS) => {
		setCustomUnit(nextUnit)
		if (valueType === 'string') {
			onChange(`custom-${customValue}-${nextUnit}` as unknown as T)
		} else {
			const recomposed = recomposeValue(customValue, nextUnit, baseUnit)
			onChange(recomposed as unknown as T)
		}
	}

	// Preset value formatted for standard Select select
	const selectValue = useMemo(() => {
		if (isCustomActive) {
			return 'custom'
		}
		if (value === null || value === undefined || value === '') {
			return ''
		}
		return String(value)
	}, [value, isCustomActive])

	return (
		<div className='w-full'>
			<div className='flex items-center gap-2 w-full'>
				<div className={isCustomActive ? 'w-[140px] shrink-0' : 'flex-1 min-w-0'}>
					<Select value={selectValue} onValueChange={handleSelectChange} disabled={disabled}>
						<SelectTrigger
							id={dataTestId}
							data-testid={dataTestId}
							className='w-full'
							aria-invalid={isCustomActive ? undefined : ariaInvalid}
							{...kbdFieldProps('select')}>
							<SelectValue placeholder={placeholder} />
						</SelectTrigger>
						<SelectContent className='z-[10001]'>
							{options.map(opt => (
								<SelectItem
									key={String(opt.value)}
									value={String(opt.value)}
									data-testid={dataTestId ? `${dataTestId}-option-${opt.value}` : undefined}>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				{isCustomActive && (
					<div className='flex items-center gap-2 flex-1 min-w-0'>
						<Input
							type='number'
							min='1'
							placeholder={customInputPlaceholder}
							value={customValue}
							onChange={e => handleCustomNumberChange(e.target.value)}
							disabled={disabled}
							className='h-control flex-1 text-body bg-card'
							data-testid={dataTestId ? `${dataTestId}-custom-input` : undefined}
							aria-invalid={ariaInvalid}
							{...kbdFieldProps()}
						/>
						{customUnits.length > 1 ? (
							<div className='w-28 shrink-0'>
								<Select
									value={customUnit}
									onValueChange={val => handleCustomUnitChange(val as keyof typeof SECONDS_MULTIPLIERS)}
									disabled={disabled}>
									<SelectTrigger className='w-full' {...kbdFieldProps('select')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent className='z-[10001]'>
										{customUnits.map(u => (
											<SelectItem key={u} value={u}>
												{u}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						) : (
							<span className='text-control text-muted-foreground shrink-0'>{unit}</span>
						)}
					</div>
				)}
			</div>
			{error && (
				// Custom mode offsets past the preset select so the message sits under the number box.
				<ErrorMessage message={error} className={isCustomActive ? 'ml-37' : undefined} />
			)}
		</div>
	)
}
